import { useEffect, useState, useCallback } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Super-admin Course Payments — the per-tee-time "call the course & pay" worklist
 * for every InteGolf-paid round (integolf / apex / unattached "InteGolf-direct").
 * Reseller-paid rounds live on the reseller's own portal, not here.
 *
 * Pay the course by phone/card, then Mark paid (records who/when + reference +
 * actual amount). Overdue-unpaid rows are pinned on top and flagged. Card-on-file
 * courses still require a Mark done so nothing goes unconfirmed.
 */
const money = (n, cur = 'CAD') => (n == null ? '—' : `${cur} ${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const fmtDate = v => { if (!v) return '—'; const d = new Date(String(v).slice(0, 10) + 'T00:00:00'); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
const fmtTime = v => { if (!v) return ''; const m = String(v).match(/(\d{1,2}):(\d{2})/); if (!m) return String(v); let h = Number(m[1]); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${m[2]} ${ap}` }

export default function CoursePayments() {
  const [data, setData] = useState(null)
  const [counts, setCounts] = useState({ unpaid: 0, overdue: 0 })
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('unpaid')
  const [page, setPage] = useState(1)
  const pageSize = 25
  const [busy, setBusy] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    tournamentApi.get('/admin/resellers/course-payments', { params: { search: search || undefined, status, page, pageSize } })
      .then(r => { setData(r.data); setCounts(r.data.counts || { unpaid: 0, overdue: 0 }); setErr(null) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [search, status, page])
  useEffect(() => { load() }, [load])

  async function markPaid(row, form) {
    setBusy(row.bookingRoundId); setErr(null)
    try {
      await tournamentApi.post(`/admin/resellers/course-payments/${row.bookingRoundId}/pay`, {
        reference: form.reference || null,
        amountPaid: form.amountPaid === '' ? null : Number(form.amountPaid),
        note: form.note || null,
      })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }
  async function undo(row) {
    if (!window.confirm('Undo — mark this tee time as unpaid again?')) return
    setBusy(row.bookingRoundId); setErr(null)
    try { await tournamentApi.post(`/admin/resellers/course-payments/${row.bookingRoundId}/undo`, {}); load() }
    catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }
  async function toggleCardOnFile(row) {
    setBusy('cof-' + row.coursesId); setErr(null)
    try {
      await tournamentApi.patch(`/admin/resellers/course-payments/course/${row.coursesId}/card-on-file`, { cardOnFile: !row.cardOnFile })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  const rows = data?.rows || []
  const total = data?.total || 0
  const pages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <AdminShell title="Course Payments">
      <div style={{ marginBottom: 16 }}>
        <p style={{ color: '#64748b', fontSize: 13, margin: 0, maxWidth: 760, lineHeight: 1.5 }}>
          Tee times InteGolf owes the course for. Call the course, pay for the round, then Mark paid with a reference.
          Overdue (past, still unpaid) are pinned on top. Card-on-file courses still need a Mark done so every charge is confirmed.
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder="Search course, player, or reseller…"
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, minWidth: 280 }}
        />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}>
          <option value="unpaid">Unpaid</option>
          <option value="overdue">Overdue</option>
          <option value="paid">Paid</option>
          <option value="">All</option>
        </select>
        <span style={{ fontSize: 12.5, color: '#64748b' }}>
          <b style={{ color: '#b45309' }}>{counts.overdue}</b> overdue · <b>{counts.unpaid}</b> unpaid
        </span>
      </div>

      {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{err}</div>}

      {loading && data === null ? <div style={{ color: '#64748b' }}>Loading…</div>
        : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b' }}>
            No tee times match. Nothing to pay right now.
          </div>
        ) : (
          <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 160px 150px 120px 130px', gap: 8, padding: '10px 14px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.4, textTransform: 'uppercase' }}>
              <div>Date / time</div><div>Course</div><div>Main player</div><div>Reseller</div><div>Expected</div><div>Status</div>
            </div>
            {rows.map(row => (
              <Row key={row.bookingRoundId} row={row} busy={busy} onPay={markPaid} onUndo={undo} onToggleCof={toggleCardOnFile} />
            ))}
          </div>
        )}

      {/* Paging */}
      {total > pageSize && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={pgBtn(page <= 1)}>← Prev</button>
          <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))} style={pgBtn(page >= pages)}>Next →</button>
        </div>
      )}
    </AdminShell>
  )
}

function Row({ row, busy, onPay, onUndo, onToggleCof }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ reference: '', amountPaid: row.expectedCost ?? '', note: '' })
  const rowBusy = busy === row.bookingRoundId
  const cofBusy = busy === ('cof-' + row.coursesId)
  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5, width: '100%', boxSizing: 'border-box' }

  return (
    <div style={{ borderTop: '1px solid #f1f5f9', background: row.isOverdue && !row.isPaid ? '#fffbeb' : '#fff', borderLeft: row.isOverdue && !row.isPaid ? '3px solid #f59e0b' : '3px solid transparent' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 160px 150px 120px 130px', gap: 8, padding: '11px 14px', alignItems: 'center', fontSize: 13 }}>
        <div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{fmtDate(row.playDate)}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{fmtTime(row.teeTime)}</div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.courseName || `Course #${row.coursesId}`}</div>
          <label style={{ fontSize: 11, color: row.cardOnFile ? '#0369a1' : '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: cofBusy ? 'default' : 'pointer', marginTop: 2 }}>
            <input type="checkbox" checked={row.cardOnFile} disabled={cofBusy} onChange={() => onToggleCof(row)} style={{ width: 13, height: 13 }} />
            card on file
          </label>
        </div>
        <div style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.mainPlayer || '—'} <span style={{ color: '#94a3b8' }}>×{row.golfers}</span>
        </div>
        <div style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#475569' }}>{row.resellerName || '—'}</div>
        <div style={{ fontWeight: 600 }}>{money(row.expectedCost, row.currency)}</div>
        <div>
          {row.isPaid ? (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#dcfce7', color: '#166534' }}>Paid</span>
          ) : row.isOverdue ? (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>Overdue</span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#e2e8f0', color: '#475569' }}>Unpaid</span>
          )}
        </div>
      </div>
      <div style={{ padding: '0 14px 12px', display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
        {row.isPaid ? (
          <>
            <span style={{ fontSize: 12, color: '#475569' }}>
              Paid {row.paidAt ? fmtDate(row.paidAt) : ''}{row.amountPaid != null ? ` · ${money(row.amountPaid, row.currency)}` : ''}{row.reference ? ` · ref ${row.reference}` : ''}
            </span>
            <button onClick={() => onUndo(row)} disabled={rowBusy} style={ghostBtn}>Undo</button>
          </>
        ) : !open ? (
          <button onClick={() => setOpen(true)} style={primaryBtn}>Mark paid</button>
        ) : (
          <div style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fbfcfe' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, alignItems: 'end' }}>
              <div>
                <label style={lbl}>Amount paid ({row.currency})</label>
                <input type="number" min="0" step="0.01" value={form.amountPaid} onChange={e => setForm(f => ({ ...f, amountPaid: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={lbl}>Reference #</label>
                <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="confirmation / receipt" style={inp} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Note (optional)</label>
                <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button onClick={() => setOpen(false)} disabled={rowBusy} style={ghostBtn}>Cancel</button>
              <button onClick={() => onPay(row, form)} disabled={rowBusy} style={primaryBtn}>{rowBusy ? 'Saving…' : 'Save — mark paid'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const lbl = { fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }
const primaryBtn = { padding: '7px 14px', fontSize: 13, fontWeight: 600, border: 'none', background: '#0f172a', color: '#fff', borderRadius: 6, cursor: 'pointer' }
const ghostBtn = { padding: '7px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 6, cursor: 'pointer' }
const pgBtn = (disabled) => ({ padding: '7px 14px', fontSize: 13, border: '1px solid #cbd5e1', background: disabled ? '#f1f5f9' : '#fff', color: disabled ? '#94a3b8' : '#0f172a', borderRadius: 6, cursor: disabled ? 'default' : 'pointer' })
